import { Field, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, FormItem, Loading, Stack, TextInput } from '@carbon/react';
import { CurrencyEnum, defaultCurrencyFor } from '@restavo/shared/enums/currency';
import CurrencySelect from 'src/components/Form/CurrencySelect';
import DndUploader from 'src/components/Uploader/DNDUploader';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useMemberships } from 'src/hooks/useOrganizations';
import { orgRestaurantCreate } from 'src/services/apis/tenantRequests';
import { RestaurantCreateDto } from 'src/services/apis/types';
import { deriveSlug } from 'src/utils/slug';
import StepLayout from './StepLayout';
import { restaurantSchema } from './schemas';

const RestaurantStep = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation('authentication');
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships, isLoading: membershipsPending } = useMemberships();
  const [logo, setLogo] = useState({ fileId: '', blurhash: '' });
  const [error, setError] = useState<Error | null>(null);

  if (membershipsPending) {
    return <Loading withOverlay description={t('onboarding.loading')} />;
  }

  const active = memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0];

  async function onSubmit(values: { name: string; address: string; currency: CurrencyEnum }) {
    if (!active) {
      setError(new Error(t('onboarding.organization.missingMembership')));
      return;
    }
    try {
      // The generated RestaurantCreateDto lags the runtime contract: the restaurant
      // entity validates a NON-EMPTY address on create (observed 422 otherwise), but
      // the Swagger DTO omits the field, so the cast is required.
      await orgRestaurantCreate(
        {
          activated: true,
          address: values.address,
          countryCode: 'VN',
          cover: '',
          coverBlurhash: '',
          currency: values.currency,
          latitude: 0,
          longitude: 0,
          logo: logo.fileId || '',
          logoBlurhash: logo.blurhash,
          name: values.name,
          slug: deriveSlug(values.name).slice(0, 32),
          slogan: '',
        } as RestaurantCreateDto,
        { orgSlug: active.slug }
      );
      onDone();
    } catch (e) {
      setError(e as Error);
    }
  }

  return (
    <Formik
      initialValues={{ name: '', address: '', currency: defaultCurrencyFor('VN') }}
      validationSchema={toFormikValidationSchema(restaurantSchema(t))}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, isValid, dirty, values, setFieldValue }) => (
        <StepLayout
          title={t('onboarding.restaurant.title')}
          subtitle={t('onboarding.restaurant.subtitle')}
          help={t('onboarding.restaurant.help')}
          error={error}
          onSubmit={handleSubmit}
          actions={
            <Button type="submit" disabled={isSubmitting || !isValid || !dirty || !active}>
              {t('onboarding.continue')}
            </Button>
          }
        >
          <Stack gap={5}>
            <FormItem>
              <p className="cds--file--label">{t('onboarding.restaurant.logo')}</p>
              <DndUploader
                useCropper
                initialValue={null}
                cropperAspectRatio={1}
                previewAspectRatio="1x1"
                onChange={({ fileId, blurhash }) => setLogo({ fileId, blurhash })}
              />
            </FormItem>
            <Field name="name">
              {({ field, meta }) => (
                <TextInput
                  id="restaurant-name"
                  labelText={t('onboarding.restaurant.name')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
            <Field name="address">
              {({ field, meta }) => (
                <TextInput
                  id="restaurant-address"
                  labelText={t('onboarding.restaurant.address')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
            <CurrencySelect
              id="currency"
              labelText={t('onboarding.restaurant.currency')}
              helperText={t('onboarding.restaurant.currencyHelp')}
              value={values.currency}
              onChange={(value) => setFieldValue('currency', value)}
            />
          </Stack>
        </StepLayout>
      )}
    </Formik>
  );
};

export default RestaurantStep;
